import { useState } from 'react'
import { Eye, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { ClientEngagementModal } from '../components/ClientEngagementModal'
import { AdminRequestDetailModal } from './AdminRequestDetailPage'
import { useAdminRequests } from '../hooks/useAdminRequests'
import { useAdminClientEngagements } from '../hooks/useAdminClientEngagements'
import { requestService } from '@/features/request/services/requestService'
import { buildLineItemsFromRequest } from '../lib/buildLineItemsFromRequest'
import type { RequestStatus, ServiceRequestDocument } from '@/types'
import type { EngagementFormValues } from '../types'

const STATUS_BADGE: Record<
  RequestStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  pending:   { variant: 'secondary',   label: 'Pending'   },
  reviewed:  { variant: 'default',     label: 'Reviewed'  },
  declined:  { variant: 'destructive', label: 'Declined'  },
  converted: { variant: 'outline',     label: 'Converted' },
}

function formatDate(val: unknown): string {
  if (!val) return '—'
  if (val instanceof Date) return val.toLocaleDateString()
  // Firestore Timestamp
  if (typeof val === 'object' && val !== null && 'toDate' in val) {
    return (val as { toDate: () => Date }).toDate().toLocaleDateString()
  }
  return '—'
}

export function AdminRequestsPage() {
  const { requests, loading, error, updateStatus } = useAdminRequests()
  const { members, equipment, createEngagement, lookupUserByEmail, createClientUser } = useAdminClientEngagements()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [engageModalOpen, setEngageModalOpen] = useState(false)
  const [convertInitialValues, setConvertInitialValues] = useState<Partial<EngagementFormValues> | undefined>()
  const [convertAvailableMemberIds, setConvertAvailableMemberIds] = useState<string[]>([])

  async function handleConvertToEngagement(request: ServiceRequestDocument & { id: string }) {
    const serviceNames = [...new Set(request.services.map((s) => s.subType))].join(' + ')
    const firstDate = request.services.find((s) => s.date)?.date ?? ''
    let lineItems: EngagementFormValues['lineItems'] = []
    let discountPercent = request.discountAppliedPercent ?? 0
    try {
      const catalog = await requestService.getCatalog()
      const built = buildLineItemsFromRequest(request, catalog.offerings, catalog.addons)
      lineItems = built.lineItems
      discountPercent = built.discountPercent
    } catch (err) {
      console.error('Failed to load catalog for conversion; opening modal with empty line items', err)
    }
    setConvertInitialValues({
      clientEmail: request.email,
      clientName: `${request.firstName} ${request.lastName}`,
      title: serviceNames || `${request.firstName} ${request.lastName} — Service Package`,
      scheduledDate: firstDate,
      sourceRequestId: request.id,
      status: 'scheduled',
      lineItems,
      discountPercent,
      assignedMemberIds: request.availableProviderMemberIds ?? [],
    })
    setConvertAvailableMemberIds(request.availableProviderMemberIds ?? [])
    setSelectedId(null)
    setEngageModalOpen(true)
  }

  return (
    <>
      <AdminListPage
        title="Service Requests"
        error={error}
        loading={loading}
        skeletonRows={5}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Services</TableHead>
              <TableHead>Est. Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <MultiButtonGroup>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    No requests yet.
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell className="font-medium">
                      {req.firstName} {req.lastName}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{req.email}</TableCell>
                    <TableCell className="text-sm">{req.services.length}</TableCell>
                    <TableCell className="font-mono text-sm">{req.quoteTotal}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE[req.status].variant}>
                        {STATUS_BADGE[req.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(req.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end">
                        <MultiButton
                          variant="outline"
                          size="sm"
                          items={[
                            {
                              id: 'view',
                              icon: Eye,
                              label: 'View',
                              onClick: () => setSelectedId(req.id),
                            },
                            ...(req.status !== 'declined' && req.status !== 'converted' ? [{
                              id: 'decline',
                              icon: XCircle,
                              label: 'Decline',
                              onClick: () => {
                                if (confirm(`Decline request from ${req.firstName} ${req.lastName}?`)) {
                                  void updateStatus(req.id, 'declined')
                                }
                              },
                            }] : []),
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </MultiButtonGroup>
        </Table>
      </AdminListPage>

      <AdminRequestDetailModal
        requestId={selectedId}
        open={selectedId !== null}
        onOpenChange={(open) => { if (!open) setSelectedId(null) }}
        onStatusChange={updateStatus}
        onConvertToEngagement={handleConvertToEngagement}
        members={members}
      />

      <ClientEngagementModal
        open={engageModalOpen}
        onOpenChange={setEngageModalOpen}
        initialValues={convertInitialValues}
        members={members}
        equipment={equipment}
        availableMemberIds={convertAvailableMemberIds}
        onSave={async (data) => {
          await createEngagement(data)
          if (data.sourceRequestId) {
            await updateStatus(data.sourceRequestId, 'converted')
          }
        }}
        onLookupUser={lookupUserByEmail}
        onCreateClient={createClientUser}
      />
    </>
  )
}
